// Investor Safe reader. Phase 6C — fetches the per-investor
// persons/{privyDid}/safe.yaml so the subscribe-fund-flow widget
// knows the destinationSafe to pass into Sale.purchase.
//
// Safe provisioning is Phase 5 §5.6's responsibility; this hook just
// reads the result. If the Safe is missing, the widget surfaces a hard
// error (per spec §P6C-3 — no fallback to wallet).

import { useEffect, useState } from 'react';
import { parse as yamlParse } from 'yaml';
import { api } from '@rmint/shell';

export type InvestorSafe = {
  privyDid: string;
  safeAddress: `0x${string}`;
  chainId: number;
  deployedAt: string;
};

export async function fetchInvestorSafe(privyDid: string): Promise<InvestorSafe | null> {
  const res = await api.get(
    `/dossier/persons/${encodeURIComponent(privyDid)}/safe.yaml`,
    { responseType: 'text' },
  );
  if (res.status === 404) return null;
  if (res.status >= 400) {
    throw new Error(`safe read failed: ${res.status}`);
  }
  const text = typeof res.data === 'string' ? res.data : '';
  return yamlParse(text) as InvestorSafe;
}

export type InvestorSafeHook = {
  loading: boolean;
  safe: InvestorSafe | null;
  error: Error | null;
};

export function useInvestorSafe(privyDid: string | undefined): InvestorSafeHook {
  const [loading, setLoading] = useState<boolean>(Boolean(privyDid));
  const [safe, setSafe] = useState<InvestorSafe | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!privyDid) {
      setLoading(false);
      setSafe(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await fetchInvestorSafe(privyDid);
        if (!cancelled) {
          setSafe(result);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e as Error);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [privyDid]);

  return { loading, safe, error };
}
